import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { getRaffleById, getRaffleEntries, updateRaffleStatus, drawRaffle } from "@/services/raffleService";
import type { VehicleRaffle, RaffleStatus } from "@/types/raffle";
import { queryKeys } from "@/lib/queryKeys";
import { ArrowLeft, Car, Gift, Trophy, Calendar, Users, Ticket } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS: Record<RaffleStatus, string> = {
  upcoming: "Próxima",
  active: "Activa",
  drawing: "En sorteo",
  completed: "Finalizada",
  cancelled: "Cancelada",
};

function formatDate(value: VehicleRaffle["drawDate"] | VehicleRaffle["endDate"]): string {
  if (value == null) return "—";
  const d = value instanceof Date ? value : "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function" ? (value as { toDate: () => Date }).toDate() : "seconds" in value ? new Date((value as { seconds: number }).seconds * 1000) : null;
  return d ? d.toLocaleDateString("es-DO", { day: "numeric", month: "short", year: "numeric" }) : "—";
}

function entryDate(entry: { entryDate: unknown }): string {
  const v = entry.entryDate;
  if (v instanceof Date) return v.toLocaleDateString("es-DO");
  if (v && typeof (v as { toDate?: () => Date }).toDate === "function") return (v as { toDate: () => Date }).toDate().toLocaleDateString("es-DO");
  if (v && typeof (v as { seconds?: number }).seconds === "number") return new Date((v as { seconds: number }).seconds * 1000).toLocaleDateString("es-DO");
  return "—";
}

export function RaffleDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { data: raffle, isLoading } = useQuery({
    queryKey: queryKeys.raffle(id!),
    queryFn: () => getRaffleById(id!),
    enabled: Boolean(id),
  });
  const { data: entries = [] } = useQuery({
    queryKey: queryKeys.raffleEntries(id!),
    queryFn: () => getRaffleEntries(id!),
    enabled: Boolean(id),
  });

  const setStatus = async (status: RaffleStatus) => {
    if (!id) return;
    try {
      await updateRaffleStatus(id, status);
      toast.success(`Rifa ${status === "active" ? "activada" : "cancelada"}`);
      await queryClient.invalidateQueries({ queryKey: queryKeys.raffles });
      await queryClient.invalidateQueries({ queryKey: queryKeys.raffle(id) });
    } catch {
      toast.error("No se pudo actualizar el estado");
    }
  };

  const handleDraw = async () => {
    if (!id) return;
    if (!confirm("¿Realizar sorteo ahora? Se elegirá un ganador aleatorio entre las entradas.")) return;
    try {
      const { winningNumber, winner } = await drawRaffle(id);
      toast.success(`Ganador: ${winner.userName} (número ${winningNumber})`);
      await queryClient.invalidateQueries({ queryKey: queryKeys.raffles });
      await queryClient.invalidateQueries({ queryKey: queryKeys.raffle(id) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.raffleEntries(id) });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al realizar el sorteo");
    }
  };

  if (!id) return null;
  if (isLoading) return <p className="text-muted-foreground">Cargando…</p>;
  if (!raffle) return <p className="text-muted-foreground">Rifa no encontrada</p>;

  const prizeName = raffle.prizeType === "vehicle" ? raffle.vehicleName : raffle.customItem?.name;
  const img = raffle.prizeType === "vehicle" ? raffle.vehicleImage : raffle.customItem?.image;
  const sold = Object.keys(raffle.selectedNumbers ?? {}).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/raffles">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-2xl font-semibold truncate">{raffle.title}</h1>
          <p className="text-muted-foreground">Detalle y participantes</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Rifa</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {img && (
              <img src={img} alt="" className="h-40 w-full rounded-md object-cover border border-border" />
            )}
            <div className="flex items-center gap-2">
              <Badge>{STATUS_LABELS[raffle.status]}</Badge>
              {raffle.isFeatured && <Badge variant="secondary">Destacada</Badge>}
            </div>
            <p className="text-sm text-muted-foreground">{raffle.description}</p>
            <div className="flex items-center gap-2 text-sm">
              {raffle.prizeType === "vehicle" ? <Car className="h-4 w-4" /> : <Gift className="h-4 w-4" />}
              <span className="font-medium">{prizeName ?? "—"}</span>
              {raffle.prizeValue != null && (
                <span className="text-muted-foreground">RD$ {raffle.prizeValue.toLocaleString()}</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-1.5">
                <Ticket className="h-4 w-4 text-muted-foreground" />
                <span>{sold} / {raffle.totalNumbers} números</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span>{raffle.totalParticipants} participantes</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>Sorteo: {formatDate(raffle.drawDate)}</span>
              </div>
            </div>
            {raffle.winner && (
              <div className="rounded-lg border bg-muted/50 p-3">
                <div className="flex items-center gap-2 font-medium">
                  <Trophy className="h-4 w-4 text-amber-500" />
                  Ganador: {raffle.winner.userName} (#{raffle.winningNumber})
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-2">
              {raffle.status === "upcoming" && (
                <Button size="sm" onClick={() => setStatus("active")}>Activar rifa</Button>
              )}
              {(raffle.status === "upcoming" || raffle.status === "active") && (
                <Button size="sm" variant="outline" onClick={() => setStatus("cancelled")}>Cancelar</Button>
              )}
              {raffle.status === "active" && entries.length > 0 && (
                <Button size="sm" variant="secondary" onClick={handleDraw}>Realizar sorteo</Button>
              )}
              <Button size="sm" variant="outline" asChild>
                <Link to={`/raffles/edit/${id}`}>Editar</Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Participantes ({entries.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {entries.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aún no hay participantes</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-mono font-medium">#{e.selectedNumber}</TableCell>
                      <TableCell>{e.userName}</TableCell>
                      <TableCell className="text-muted-foreground">{e.userEmail ?? "—"}</TableCell>
                      <TableCell className="text-muted-foreground">{entryDate(e)}</TableCell>
                      <TableCell>
                        {e.isWinner ? <Badge>Ganador</Badge> : e.isPaid ? <span className="text-muted-foreground">Pagado</span> : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
